import { ReactNode } from "react";

import { AppAction } from "./permissions";
import { usePermission } from "./usePermission";

type IfRoleProps = {
  action: AppAction;
  /** Rendered when the user lacks permission. Defaults to null (hidden). */
  fallback?: ReactNode;
  children: ReactNode;
};

/**
 * Conditionally renders children based on whether the user has permission
 * for the given action.
 *
 * Use this to hide UI elements (buttons, menu items, sections) that the
 * current user cannot act on.
 *
 * @example
 * <IfRole action="createConnection">
 *   <AddConnectorButton />
 * </IfRole>
 *
 * @example
 * <IfRole action="manageBilling" fallback={<ReadOnlyBillingView />}>
 *   <BillingActions />
 * </IfRole>
 */
export function IfRole({ action, children, fallback = null }: IfRoleProps) {
  const allowed = usePermission(action);
  return allowed ? <>{children}</> : <>{fallback}</>;
}
